import { t } from 'i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from '@/lib/v2/components';

import { useCampaignsService } from '@/src/infrastructure/Protocol/Campaigns/useCampaignsService';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import {
  atomActionsList,
  atomDetailCampaign,
  atomLoadingProcess,
  atomMoveSuccess,
  atomSelectedActionsRowsWithID,
  atomTotalSelectedRows,
  atomTotalsProcess,
} from '@/src/modules/CampaignsModule/atoms/campaignAtom';
import {
  IDuplicateCampaignActions,
  IEditActionPayload,
  IFilterByTable,
  TActionID,
} from '@/src/modules/CampaignsModule/interfaces/CampaignActions';
import {
  ECampaignsTypeOptions,
  IMoveCampaignPayload,
  IOrderByTable,
  TCampaignID,
} from '@/src/modules/CampaignsModule/interfaces/Campaigns';

const TRANSLATE_MODAL_DELETE = 'CAMPAIGN_ACTIONS_MAIN.DELETE_ACTIONS.TOAST_MESSAGES';
const TRANSLATE_MODAL_DUPLICATE = 'CAMPAIGN_ACTIONS_MAIN.DUPLICATE_ACTIONS.TOAST_MESSAGES';
const TRANSLATE_MODAL_EDIT = 'CAMPAIGN_ACTIONS_MAIN.EDIT_ACTIONS.TOAST_MESSAGES';
const TRANSLATE_MODAL_MOVE = 'CAMPAIGN_ACTIONS_MAIN.MOVE_ACTIONS.TOAST_MESSAGES';

interface useCampaignActions {
  campaignID: TCampaignID;
  campaignActionsStateContext?: {
    pageSize: number;
    pageIndex: number;
    filterBy: IFilterByTable;
    orderBy: IOrderByTable;
  };
}

export const useCampaignActions = ({
  campaignID,
  campaignActionsStateContext = {
    filterBy: {},
    orderBy: { sortField: '', sortOrder: '' },
    pageIndex: 0,
    pageSize: 0,
  },
}: useCampaignActions) => {
  const navigate = useNavigate();

  const service = useCampaignsService();
  const serviceV1 = useService();

  const { pageSize, pageIndex, filterBy, orderBy } = campaignActionsStateContext;

  const detailCampaign = useAtomValue(atomDetailCampaign);
  const setActionsList = useSetAtom(atomActionsList);
  const setTotalsCampaignList = useSetAtom(atomTotalsProcess);
  const setLoadingProcess = useSetAtom(atomLoadingProcess);

  const totalSelectedRows = useAtomValue(atomTotalSelectedRows);
  const [loading, setLoading] = useState<boolean>(false);
  const selectedRowsWithID = useAtomValue(atomSelectedActionsRowsWithID);
  const setMoveSuccess = useSetAtom(atomMoveSuccess);

  // const { pageSize, pageIndex, filterBy, orderBy } = useCampaignActionsStateContext();

  const getList = useCallback(async () => {
    if (!detailCampaign?.campaignTypeId) return;

    setLoadingProcess(true);

    let orderByCampaignTypeId = { ...orderBy };
    if (detailCampaign?.campaignTypeId === ECampaignsTypeOptions.AUTOMATIC) {
      orderByCampaignTypeId = { sortField: 'channel', sortOrder: 'desc' };
    }

    const payload = {
      campaignId: campaignID,
      page: pageIndex,
      limit: pageSize,
      ...filterBy,
      ...orderByCampaignTypeId,
    };

    const response = await service.getActionsByCampaign(payload);

    setActionsList(response?.actions || []);
    setTotalsCampaignList({
      total: response?.total || 0,
      partial: response?.partial || 0,
    });
    setLoadingProcess(false);
  }, [
    campaignID,
    detailCampaign?.campaignTypeId,
    filterBy,
    orderBy,
    pageIndex,
    pageSize,
    service,
    setActionsList,
    setLoadingProcess,
    setTotalsCampaignList,
  ]);

  const editAction = useCallback(
    async ({ name, actionId }: { name: string; actionId: number }) => {
      if (!actionId) return;

      setLoading(true);

      const campaignPayload: IEditActionPayload = {
        campaignElementId: Number(actionId),
        name: name.replace(/\s+/g, ' ').trim(),
      };
      const { statusOK } = await service.editAction(campaignPayload);
      setLoading(false);

      toast({
        title: statusOK
          ? t(`${TRANSLATE_MODAL_EDIT}.SUCCESS.title`)
          : t(`${TRANSLATE_MODAL_EDIT}.ERROR.title`),
        body: statusOK
          ? t(`${TRANSLATE_MODAL_EDIT}.SUCCESS.message`)
          : t(`${TRANSLATE_MODAL_EDIT}.ERROR.message`),
        variant: statusOK ? 'success' : 'error',
      });
      return statusOK;
    },
    [service]
  );

  const moveAction = useCallback(
    async (payload: IMoveCampaignPayload) => {
      /** cspell:disable */
      const isSingle: boolean = payload?.elementosId?.length === 1;
      /** cspell:enable */
      setLoading(true);

      const { statusOK } = await serviceV1.moveV1Action(payload);
      setLoading(false);

      if (statusOK) {
        setMoveSuccess(true);
        navigate(`/v2/campaigns/${payload.campaniaId}`);
        return;
      }

      toast({
        title: isSingle
          ? t(`${TRANSLATE_MODAL_MOVE}.ERROR.SINGLE.title`)
          : t(`${TRANSLATE_MODAL_MOVE}.ERROR.MULTIPLE.title`),
        body: isSingle
          ? t(`${TRANSLATE_MODAL_MOVE}.ERROR.SINGLE.message`)
          : t(`${TRANSLATE_MODAL_MOVE}.ERROR.MULTIPLE.message`),
        variant: 'error',
      });
    },
    [navigate, serviceV1, setMoveSuccess]
  );

  const deleteAction = useCallback(async () => {
    const isSingle: boolean = totalSelectedRows === 1;

    if (totalSelectedRows === 0) {
      toast({
        title: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.title`)
          : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.title`),
        body: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.message`)
          : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.message`),
        variant: 'error',
      });
      return;
    }

    setLoading(true);

    const { statusOK } = await serviceV1.deleteV1Actions(selectedRowsWithID);

    if (!statusOK) {
      toast({
        title: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.title`)
          : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.title`),
        body: isSingle
          ? t(`${TRANSLATE_MODAL_DELETE}.ERROR.SINGLE.message`)
          : t(`${TRANSLATE_MODAL_DELETE}.ERROR.MULTIPLE.message`),
        variant: 'error',
      });
      return;
    }

    toast({
      title: isSingle
        ? t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.SINGLE.title`)
        : t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.MULTIPLE.title`),
      body: isSingle
        ? t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.SINGLE.message`)
        : t(`${TRANSLATE_MODAL_DELETE}.SUCCESS.MULTIPLE.message`),
      variant: 'success',
    });

    setLoading(false);
  }, [selectedRowsWithID, serviceV1, totalSelectedRows]);

  const duplicateAction = useCallback(
    async ({ name, actionId }: { name: string; actionId: TActionID }) => {
      if (!actionId) return;
      setLoading(true);
      const campaignPayload: IDuplicateCampaignActions = {
        actionId: Number(actionId),
        name,
      };
      const { statusOK, actionIdDuplicate } = await serviceV1.duplicateV1Actions(campaignPayload);
      setLoading(false);
      toast({
        title: statusOK
          ? t(`${TRANSLATE_MODAL_DUPLICATE}.SUCCESS.title`)
          : t(`${TRANSLATE_MODAL_DUPLICATE}.ERROR.title`),
        body: statusOK
          ? t(`${TRANSLATE_MODAL_DUPLICATE}.SUCCESS.message`)
          : t(`${TRANSLATE_MODAL_DUPLICATE}.ERROR.message`),
        variant: statusOK ? 'success' : 'error',
      });
      return actionIdDuplicate;
    },
    [serviceV1]
  );

  return { getList, deleteAction, loading, duplicateAction, editAction, moveAction };
};
